import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  Observable,
  gql,
} from "@apollo/client";
import { InMemoryCache, defaultDataIdFromObject } from "@apollo/client/cache";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import ActionCable from "actioncable";
import { ActionCableLink } from "graphql-ruby-client";
import { GetCredentialDocument } from "./graphql/generated/definitions";


const cache = new InMemoryCache({
  dataIdFromObject: (object) => {
    switch (object.__typename) {
      case "Credential":
        return object.accessToken;
      default:
        return defaultDataIdFromObject(object);
    }
  },
  typePolicies: {
    Query: {
      fields: {
        activities: {
          keyArgs: false,
          merge(existing = {collection: [] }, incoming) {
            return {
              collection: [...existing.collection, ...incoming.collection],
              metadata: incoming.metadata,
            };
          },
        },
      },
    },
  },
});

const cable = ActionCable.createConsumer("/cable");

const hasSubscriptionOperation = ({ query: { definitions } }) => {
  return definitions.some(
    ({ kind, operation }) =>
      kind === "OperationDefinition" && operation === "subscription"
  );
};

const request = async (operation) => {
  const credentialsJSON = localStorage.getItem("credential");

  if (credentialsJSON) {
    try {
      const { accessToken, client, expiry, tokenType, uid } =
        JSON.parse(credentialsJSON);

      operation.setContext({
        headers: {
          accessToken,
          tokenType,
          client,
          expiry,
          uid,
        },
      });
    } catch {
      localStorage.removeItem("credential");
    }
  }
};

const requestLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      let handle;
      Promise.resolve(operation)
        .then((operation) => request(operation))
        .then(() => {
          handle = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: (...args) => {
              observer.complete(...args);
            },
          });
        })
        .catch(observer.error.bind(observer));

      return () => {
        if (handle) handle.unsubscribe();
      };
    })
);

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 10000,
    jitter: true,
  },
  attempts: {
    max: Infinity,
    retryIf: (error) => {
      return !!error.message.match(/Network/);
    },
  },
});

const httpLink = new HttpLink({
  uri: "/graphql",
  credentials: "include",
});

const client = new ApolloClient({
  typeDefs: gql`
    type Notification {
      body: String
      id: ID!
      status: String!
      title: String!
    }
    type Credential {
      accessToken: String!
      uid: String!
      tokenType: String!
      client: String!
      expiry: Int!
    }
  `,
  resolvers: {
    Query: {
      credential: () => {
        const credentialsJSON = localStorage.getItem("credential");

        if (credentialsJSON) {
          return { __typename: "Credential", ...JSON.parse(credentialsJSON) };
        }
        return null;
      },
    },
    Mutation: {
      setCredential: async (_, credential, { cache }) => {
        localStorage.setItem("credential", JSON.stringify(credential));

        cache.writeQuery({
          query: GetCredentialDocument,
          data: { credential: { __typename: "Credential", ...credential } },
        });
        return null;
      },
      deleteCredential: async (_, _params, { cache }) => {
        localStorage.removeItem("credential");

        cache.writeQuery({
          query: GetCredentialDocument,
          data: { credential: null },
        });
        return null;
      },
    },
  },
  link: ApolloLink.split(
    hasSubscriptionOperation,
    new ActionCableLink({ cable }),
    ApolloLink.from([
      onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors) {
          graphQLErrors.map(({ message, locations, path }) => {
            console.log(
              `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
            );
          });
        }
        if (networkError) {
          console.log(`[Network error]: ${networkError}`);
        }
      }),
      requestLink,
      retryLink,
      httpLink,
    ])
  ),
  cache,
});

export default client;
